import * as React from 'react';
import { tabbable } from 'tabbable';
import { throttle } from 'throttle-debounce';
import { StackingComponentProps, StackingComponentWrapper } from './StackingComponent';
import * as ViewportHelpers from './ViewportHelpers';

const ResizeThrottleTime = 100;
const ScrollThrottleTime = 15;

export enum VerticalOriginOption {
  top = 'top',
  centerY = 'centerY',
  bottom = 'bottom',
}

export enum HorizontalOriginOption {
  left = 'left',
  centerX = 'centerX',
  right = 'right',
}

export interface OriginOptions {
  vertical: VerticalOriginOption;
  horizontal: HorizontalOriginOption;
}

type OriginPositionCoordinates = {
  [key in (VerticalOriginOption | HorizontalOriginOption)]: number;
};

type SimplePosition = {
  top: number;
  left: number;
};

export interface OverlayProps extends Pick<StackingComponentProps, 'portalEventTrapOverrides' | 'stackingRootId' | 'zIndex'> {
  /**
   * Can the stacked element automatically position itself to try to fit on the screen
   */
  allowAutoPosition?: boolean;

  /**
   * The DOM element the stacked element will be anchored to.
   * Ignored if `getAnchorClientRect` is provided.
   */
  anchorEl?: HTMLElement | null;

  /**
   * The relative point on the anchor element where the overlaid stacked element will be affixed.
   * This anchor point is defined by an OriginOptions object, which has a vertical and horizontal component.
   * To specify the vertical component, use the VerticalOriginOption enum.
   * To specify the horizontal component, use the HorizontalOriginOption enum.
   * @default ```{ vertical: VerticalOriginOption.top, horizontal: HorizontalOriginOption.left }```
   */
  anchorOrigin?: OriginOptions;

  /** if true, on mount, focuses the first tabbable element -- or if none, the first focusable element -- in document order. */
  autoFocus?: boolean;

  /**
   * If true, sets `pointer-events: none` on the stacked element.
   */
  disablePointerEvents?: boolean;

  /**
   * Callback to get the ClientRect that the stacked element will be anchored to.
   * n.b. the `width` and `height` properties on the returned rect are ignored.
   * This is useful if the rectangle around which positioning should occur needs to differ from a particular element, e.g. to add a buffer.
   */
  getAnchorClientRect?: () => (ClientRect | undefined);

  /**
   * The callback fired when the scrolling root has been scrolled. A common usecase for this may be to close the overlay.
   */
  handleScrollOnScrollingRoot?: () => void;

  /**
   * The scrolling root is the container that will have scrolling event listeners attached to it by default from scrolling actions.
   * If no `scrollingRootId` is provided it will default to `document.body`.
   * A reason for setting this is so a callback is fired only when the scrolling root is scrolled, instead of the entire body.
   * @deprecated instead use `ScrollingRootContext`
   */
  scrollingRootId?: string;

  /**
   * Autopositions the overlay when the scrolling root has been scrolled.  Dependent on allowAutoPosition being true.
   */
  shouldAutoPositionOnScrollingRootScroll?: boolean;

  /**
   * The relative point on the overlaid stacked element where the stacked element will be affixed to the anchor
   * This anchor point is defined by an OriginOptions object, which has a vertical and horizontal component.
   * To specify the vertical component, use the VerticalOriginOption enum.
   * To specify the horizontal component, use the HorizontalOriginOption enum.
   * @default ```{ vertical: VerticalOriginOption.top, horizontal: HorizontalOriginOption.left }```
   */
  stackedOrigin?: OriginOptions;
}

export const ScrollingRootContext = React.createContext<HTMLElement>(document.body);
ScrollingRootContext.displayName = 'ScrollingRootContext';
function getScrollingRootElement(scrollingRootId: string | undefined, context: Overlay['context']): HTMLElement {
  const scrollingRoot: HTMLElement | null = document.getElementById(scrollingRootId!) || context || document.body;
  if (!scrollingRoot) {
    throw new Error('Could not find scrolling root.');
  }
  return scrollingRoot;
}

export class Overlay extends React.Component<OverlayProps> {
  private scrollingRootElement: HTMLElement | null = null;
  private stackingWrapper: StackingComponentWrapper;
  private onResize: () => void = throttle(ResizeThrottleTime, () => this.setPlacement());
  private onScroll: (() => void) | null;

  public static defaultProps: Required<Pick<OverlayProps, 'anchorOrigin' | 'stackedOrigin'>> = {
    anchorOrigin: {
      vertical: VerticalOriginOption.top,
      horizontal: HorizontalOriginOption.left
    },
    stackedOrigin: {
      vertical: VerticalOriginOption.top,
      horizontal: HorizontalOriginOption.left
    }
  };
  public context!: React.ContextType<typeof ScrollingRootContext>;

  public componentDidMount(): void {
    this.addScrollingRootBehavior();
    window.addEventListener('resize', this.onResize);
    const stackedEl = this.getStackedEl();
    if (this.props.autoFocus && stackedEl) {
      const tabbables = tabbable(stackedEl);
      const node = tabbables[0] || stackedEl.querySelector('[tabindex]');
      node?.focus();
    }
  }

  public componentWillUnmount(): void {
    this.removeScrollingRootBehavior();
    window.removeEventListener('resize', this.onResize);
  }

  public componentDidUpdate(prevProps: OverlayProps): void {
    this.resetScrollingRootBehaviorIfNeeded(prevProps);
    this.setPlacement();
  }

  /**
   * This adds the scrolling event listeners to the scrolling root if onScroll is not null.
   */
  private addScrollingRootBehavior(): void {
    this.setOnScroll();
    if (this.onScroll) {
      // eslint-disable-next-line deprecation/deprecation
      this.scrollingRootElement = getScrollingRootElement(this.props.scrollingRootId, this.context);
      this.scrollingRootElement.addEventListener('scroll', this.onScroll);
      this.scrollingRootElement.addEventListener('wheel', this.onScroll);
    }
  }

  /**
   * This removes the scrolling event listeners to the scrolling root if onScroll is not null.
   */
  private removeScrollingRootBehavior(): void {
    if (this.onScroll) {
      this.scrollingRootElement?.removeEventListener('scroll', this.onScroll);
      this.scrollingRootElement?.removeEventListener('wheel', this.onScroll);
      this.scrollingRootElement = null;
    }
  }

  /**
   * This resets the scrolling root behavior if handleScrollOnScrollingRoot or shouldAutoPositionOnScrollingRootScroll has changed.
   */
  private resetScrollingRootBehaviorIfNeeded(prevProps: OverlayProps): void {
    if (prevProps.handleScrollOnScrollingRoot !== this.props.handleScrollOnScrollingRoot ||
      prevProps.shouldAutoPositionOnScrollingRootScroll !== this.props.shouldAutoPositionOnScrollingRootScroll) {
      this.removeScrollingRootBehavior();
      this.addScrollingRootBehavior();
    }
  }

  /**
   * Assigns onScroll to a method if shouldAutoPositionOnScrollingRootScroll is true or handleScrollOnScrollingRoot is not null.
   * Otherwise assign it to null.
   */
  private setOnScroll(): void {
    if (this.props.shouldAutoPositionOnScrollingRootScroll || !!this.props.handleScrollOnScrollingRoot) {
      const onScroll = () => {
        if (this.props.shouldAutoPositionOnScrollingRootScroll) {
          this.setPlacement();
        }
        if (this.props.handleScrollOnScrollingRoot) {
          this.props.handleScrollOnScrollingRoot();
        }
      };
      this.onScroll = throttle(ScrollThrottleTime, onScroll);
    } else {
      this.onScroll = null;
    }
  }

  /**
   * Returns the position of the anchor element
   */
  private getAnchorPosition(): OriginPositionCoordinates {
    let anchorClientRect: ClientRect | undefined = undefined;

    if (this.props.getAnchorClientRect) {
      anchorClientRect = this.props.getAnchorClientRect();
    }

    if (anchorClientRect === undefined) {
      // This will allow stacked elements to be placed locally if no anchor element is provided
      const anchorEl = this.props.anchorEl || this.getStackedEl();
      anchorClientRect = anchorEl.getBoundingClientRect();
    }

    const { left, top, right, bottom } = anchorClientRect;

    return {
      top,
      centerY: top + (bottom - top) / 2,
      bottom,
      left,
      centerX: left + (right - left) / 2,
      right,
    };
  }

  /**
   * Returns the shape of the element that has been stacked in position coordinates. So the top left of the shape
   * is top: 0, left: 0, the right corner is top: 0, right: width, and so on.
   */
  private getStackedElCurrentShapeInPositionCoords(): OriginPositionCoordinates {
    const stackedEl = this.getStackedEl();

    return {
      top: 0,
      centerY: stackedEl.offsetHeight / 2,
      bottom: stackedEl.offsetHeight,
      left: 0,
      centerX: stackedEl.offsetWidth / 2,
      right: stackedEl.offsetWidth,
    };
  }

  /**
   * Returns the rectangle of the container
   */
  private getContainerRect(): ClientRect {
    return this.stackingWrapper.getStackingRoot().getBoundingClientRect();
  }

  /**
   * Sets the stacked element position
   */
  public setPlacement(): void {
    if (!this.stackingWrapper || !this.getStackedEl()) {
      return;
    }

    // TFSID 1083485: Some elements change their size when positioned absolutely.
    // In order to accurately set their position, we need to position them absolutely first.
    const stackedEl = this.getStackedEl();
    stackedEl.style.position = 'absolute';

    const stackedElAdjustedPosition = this.getStackedElAdjustedPosition();

    stackedEl.style.top = `${stackedElAdjustedPosition.top}px`;
    stackedEl.style.left = `${stackedElAdjustedPosition.left}px`;
    if (this.props.disablePointerEvents) {
      stackedEl.style.pointerEvents = 'none';
    }
  }

  /**
   * Gets the stacked element from stacking wrapper
   */
  private getStackedEl(): HTMLElement {
    return this.stackingWrapper.getPortalChildElement()!;
  }

  /**
   * Returns the Stacked Element position that has been autopositioned if necessary and adjusted to position
   * relative to the container
   */
  private getStackedElAdjustedPosition(): SimplePosition {
    let viewportPosition = this.getDefaultStackedElViewportPosition();

    if (this.props.allowAutoPosition) {
      viewportPosition = this.getAutoPositionIfNeeded(viewportPosition);
    }

    return this.getAdjustStackedElPositionForContainer(viewportPosition);
  }

  /**
   * Returns the stacked element viewport position from the given and default origin option props
   */
  private getDefaultStackedElViewportPosition(): SimplePosition {
    const stackedElPosition = this.getStackedElViewportPosition(this.props.anchorOrigin!, this.props.stackedOrigin!);
    return stackedElPosition;
  }

  /**
   * Adjusts the viewport position so that it can be absolutely positioned relative to the container
   */
  private getAdjustStackedElPositionForContainer(viewportPosition: SimplePosition): SimplePosition {
    const containerRect = this.getContainerRect();

    const newPosition: SimplePosition = {
      top: viewportPosition.top - containerRect.top,
      left: viewportPosition.left - containerRect.left
    };

    return newPosition;
  }

  /**
   * Using the given origins return the adjusted stacked element viewport position
   */
  private getStackedElViewportPosition(anchorOriginOptions: OriginOptions, stackedElOriginOptions: OriginOptions): SimplePosition {
    const anchorPosition = this.getAnchorPosition();
    const stackedElShape = this.getStackedElCurrentShapeInPositionCoords();

    // This gets the origin strings from the enum (eg 'top', 'left', etc)
    const anchorVerticalOriginOption = VerticalOriginOption[anchorOriginOptions.vertical];
    const anchorHorizontalOriginOption = HorizontalOriginOption[anchorOriginOptions.horizontal];
    const stackedElVerticalOriginOption = VerticalOriginOption[stackedElOriginOptions.vertical];
    const stackedElHorizontalOriginOption = HorizontalOriginOption[stackedElOriginOptions.horizontal];

    // This gets the top/left for the anchor/stackedEl origins,
    // similar to OriginPositionCoordinates.left, OriginPositionCoordinates.top, etc
    const anchorOriginTop = anchorPosition[anchorVerticalOriginOption];
    const anchorOriginLeft = anchorPosition[anchorHorizontalOriginOption];
    const stackedElOriginTop = stackedElShape[stackedElVerticalOriginOption];
    const stackedElOriginLeft = stackedElShape[stackedElHorizontalOriginOption];

    const adjustedStackedPosition: SimplePosition = {
      top: anchorOriginTop - stackedElOriginTop,
      left: anchorOriginLeft - stackedElOriginLeft,
    };

    return adjustedStackedPosition;
  }

  /**
   * This will try to autoposition the stacked element if the stacked element will appear off screen.
   * It autopositions by using the opposite origin options and getting that viewport position
   *
   * Example:
   * Anchor is at the bottom right of the screen with the anchor origin as {top, right} and the stacked el origin as {top, left}
   * Detects that it's vertically offscreen and changes the anchor origin to {bottom, right} and stacked el origin as {bottom, left}
   * Method sees that it is now not below the screen and sets the adjusted top position
   * Detects that it's horizontally offscreen and changes the anchor origin to {top, left} and stacked el origin as {top, right}
   * Method sees that it is now not to the right of the screen and sets the adjusted left position
   */
  private getAutoPositionIfNeeded(adjustedStackedElPosition: SimplePosition): SimplePosition {
    const stackedElShape = this.getStackedElCurrentShapeInPositionCoords();
    const oppositeOriginPosition = this.getOppositeOriginPosition();
    const layoutViewportSize = ViewportHelpers.getLayoutViewportSize();

    const isVerticallyOffscreen: boolean =
      adjustedStackedElPosition.top < 0 ||
      adjustedStackedElPosition.top + stackedElShape.bottom > layoutViewportSize.height;

    const isHorizontallyOffscreen: boolean =
      adjustedStackedElPosition.left < 0 ||
      adjustedStackedElPosition.left + stackedElShape.right > layoutViewportSize.width;

    if (isVerticallyOffscreen) {
      if (oppositeOriginPosition.top + stackedElShape.bottom <= layoutViewportSize.height) {
        adjustedStackedElPosition.top = Math.max(0, oppositeOriginPosition.top);
      } else if (stackedElShape.bottom <= layoutViewportSize.height) {
        adjustedStackedElPosition.top = Math.max(0, layoutViewportSize.height - stackedElShape.bottom);
      }
    }

    if (isHorizontallyOffscreen) {
      if (oppositeOriginPosition.left + stackedElShape.right <= layoutViewportSize.width) {
        adjustedStackedElPosition.left = Math.max(0, oppositeOriginPosition.left);
      } else if (stackedElShape.right <= layoutViewportSize.width) {
        adjustedStackedElPosition.left = Math.max(0, layoutViewportSize.width - stackedElShape.right);
      }
    }

    return adjustedStackedElPosition;
  }

  /**
   * Uses the opposite origins for the anchor and stacked element to return the viewport position
   */
  private getOppositeOriginPosition(): SimplePosition {
    const oppositeAnchorOriginOptions = this.getOppositeOriginsOptions(this.props.anchorOrigin!);
    const oppositeStackedElOriginOptions = this.getOppositeOriginsOptions(this.props.stackedOrigin!);
    const stackedElPosition = this.getStackedElViewportPosition(oppositeAnchorOriginOptions, oppositeStackedElOriginOptions);

    return stackedElPosition;
  }

  /**
   * Returns the opposite origin options
   */
  private getOppositeOriginsOptions(originOptions: OriginOptions): OriginOptions {
    return {
      horizontal: this.getOppositeHorizontalOption(originOptions.horizontal),
      vertical: this.getOppositeVerticalOption(originOptions.vertical)
    };
  }

  /**
   * Returns the opposite VerticalOriginOption, unless it is center.
   */
  private getOppositeVerticalOption(verticalOriginOption: VerticalOriginOption): VerticalOriginOption {
    if (verticalOriginOption === VerticalOriginOption.centerY) { return VerticalOriginOption.centerY; }
    return verticalOriginOption === VerticalOriginOption.top ? VerticalOriginOption.bottom : VerticalOriginOption.top;
  }

  /**
   * Returns the opposite HorizontalOriginOption, unless it is center.
   */
  private getOppositeHorizontalOption(horizontalOriginOption: HorizontalOriginOption): HorizontalOriginOption {
    if (horizontalOriginOption === HorizontalOriginOption.centerX) { return HorizontalOriginOption.centerX; }
    return horizontalOriginOption === HorizontalOriginOption.left ? HorizontalOriginOption.right : HorizontalOriginOption.left;
  }

  /**
   * Once the stacking wrapper mounts it returns itself and we set the placement in case the stacked element is already open
   */
  private setStackingWrapper = (stackingWrapper: StackingComponentWrapper): void => {
    this.stackingWrapper = stackingWrapper;
    this.setPlacement();
  }

  /* eslint-disable deprecation/deprecation */
  public render(): JSX.Element {
    let wrapperId: string = this.props.stackingRootId ? `${this.props.stackingRootId}-` : '';
    wrapperId = this.props.scrollingRootId ? `${wrapperId}${this.props.scrollingRootId}-` : wrapperId;

    return <StackingComponentWrapper
      children={this.props.children}
      portalEventTrapOverrides={this.props.portalEventTrapOverrides}
      ref={this.setStackingWrapper}
      stackingRootId={this.props.stackingRootId}
      wrapperTestId={`${wrapperId}Overlay`}
      zIndex={this.props.zIndex}
    />;
  }
  /* eslint-enable deprecation/deprecation */
}
Overlay.contextType = ScrollingRootContext;
